/**
 * COLUMN-FILTER COVERAGE for the List views (leads page, stage routes,
 * favorite views).
 *
 * The header column filters (`useListColumnFilters`) work in the browser over
 * the rows already loaded, and the list loads 15 rows a page as you scroll.
 * A pick therefore only checked the leads someone had scrolled past: filter
 * Counsellor = Asma on a 3,000-lead list and the table showed Asma's leads
 * from the first page or two, while the Board (server-side Filter) showed all
 * of them — "the leads are not coming".
 *
 * While a column filter is on, the container now loads the REST of the list
 * by itself, in bigger pages, so the pick is checked against every lead the
 * list holds. It stops after `COLUMN_FILTER_CHECK_STEP` rows and offers to
 * check the next batch, so a filter on a 50k-lead list never turns into an
 * open-ended download (list rows count against each user's daily API budget,
 * `security/apiGuard.ts` on the server). A sort on its own loads nothing extra.
 */
import { useCallback, useEffect, useState } from "react";
import { hashKey, type QueryKey } from "@tanstack/react-query";

/** Rows per request while a column filter is on (server cap is 1000). */
export const COLUMN_FILTER_PAGE_SIZE = 100;
/** Rows checked before asking to check more. */
export const COLUMN_FILTER_CHECK_STEP = 2000;

/**
 * True when `key` is `base` plus, at most, a trailing page-size segment —
 * i.e. only the page size changed. Lets a list keep its rows on screen while
 * the other page size loads.
 */
export function sameExceptPageSize(key: QueryKey, base: QueryKey): boolean {
  if (key.length < base.length || key.length > base.length + 1) return false;
  return hashKey(key.slice(0, base.length)) === hashKey(base);
}

export interface ColumnFilterCoverage {
  /** Still loading the rest of the list to check the filter against it. */
  checking: boolean;
  /** Stopped at the check limit with more of the list unchecked. */
  capped: boolean;
  /** Check the next `COLUMN_FILTER_CHECK_STEP` rows. */
  checkMore: () => void;
}

export function useColumnFilterCoverage({
  filtering,
  loaded,
  hasNextPage,
  isFetching,
  isError,
  isPlaceholderData = false,
  fetchNextPage,
  resetKey,
}: {
  /** A column filter is narrowing the rows (a sort alone doesn't count). */
  filtering: boolean;
  /** Rows loaded so far. */
  loaded: number;
  hasNextPage: boolean;
  /** Any request for this list in flight (next page OR a background refetch
   *  after a lead changed) — the run waits for it, then carries on. */
  isFetching: boolean;
  /** Stop on a failed page instead of retrying it in a loop. */
  isError: boolean;
  /** The rows on screen are the previous page size's, standing in while
   *  this size's first page loads. */
  isPlaceholderData?: boolean;
  fetchNextPage: (options?: { cancelRefetch?: boolean }) => unknown;
  /** Anything that makes it a different list (the main filters). */
  resetKey: string;
}): ColumnFilterCoverage {
  // The limit belongs to one list: a different list starts from one step.
  const [raised, setRaised] = useState({ key: resetKey, limit: COLUMN_FILTER_CHECK_STEP });
  const limit = raised.key === resetKey ? raised.limit : COLUMN_FILTER_CHECK_STEP;

  const more = filtering && hasNextPage && !isError;
  const capped = more && loaded >= limit;

  // One page at a time: the next request goes out only once nothing is in
  // flight for this list. Waiting on ANY fetch (not just the next page) means
  // a background refetch mid-run delays the run instead of stalling it.
  // `cancelRefetch: false` makes a second caller (the full-screen List mounts
  // a second copy of the same list) join the request in flight rather than
  // cancel and re-send it.
  useEffect(() => {
    if (!more || capped || isFetching) return;
    fetchNextPage({ cancelRefetch: false });
  }, [more, capped, isFetching, fetchNextPage]);

  const checkMore = useCallback(
    () => setRaised({ key: resetKey, limit: limit + COLUMN_FILTER_CHECK_STEP }),
    [resetKey, limit],
  );

  return {
    checking: filtering && (isPlaceholderData || (more && !capped)),
    capped,
    checkMore,
  };
}
